import { makeAutoObservable } from 'mobx';
import type { ReactNode } from 'react';

type NotificationType = 'error' | 'info';

export interface INotification {
  id: number;
  key?: string;
  message: ReactNode;
  expanded: boolean;
  type: NotificationType;
}

export interface GlobalAlertNotification {
  key: string;
  message: ReactNode;
  type: NotificationType;
}

class TopNotificationStore {
  notifications: INotification[] = [];

  globalAlertsCount = 0;

  constructor() {
    makeAutoObservable(this);
  }

  setGlobalAlertsCount = (count: number) => {
    this.globalAlertsCount = count;
  };

  get isVisible() {
    return this.notifications.length > 0;
  }

  show = (message: ReactNode, type: NotificationType) => {
    this.notifications.push({
      id: Date.now() + Math.random(),
      message,
      expanded: false,
      type,
    });
  };

  syncGlobalAlerts = (alerts: GlobalAlertNotification[]) => {
    const activeKeys = new Set(
      alerts.map((alert) => `global-alert:${alert.key}`)
    );

    this.notifications = this.notifications.filter(
      (notification) =>
        !notification.key?.startsWith('global-alert:') ||
        activeKeys.has(notification.key)
    );

    alerts.forEach((alert) => {
      const key = `global-alert:${alert.key}`;

      const existing = this.notifications.find(
        (notification) => notification.key === key
      );

      if (existing) {
        existing.message = alert.message;
        existing.type = alert.type;
        return;
      }

      this.notifications.push({
        id: Date.now() + Math.random(),
        key,
        message: alert.message,
        expanded: false,
        type: alert.type,
      });
    });
  };

  close = (id: number) => {
    this.notifications = this.notifications.filter(
      (notification) => notification.id !== id
    );
  };

  toggle = (id: number) => {
    const notification = this.notifications.find(
      (notification) => notification.id === id
    );

    if (notification) {
      notification.expanded = !notification.expanded;
    }
  };
}

export const topNotificationStore = new TopNotificationStore();